import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, BookOpen, Play, Clock, Eye, Heart, Plus,
  FileText, GraduationCap, TrendingUp, Shield, Landmark,
  Calculator, Building2, Users, Tag, ExternalLink, Pencil,
} from "lucide-react";

const Card = ({ children, className = "", onClick }: { children: React.ReactNode; className?: string; onClick?: () => void }) => (
  <div className={`p-5 rounded-lg bg-card border border-border shadow-card ${className}`} onClick={onClick}>{children}</div>
);

const CATEGORIES = [
  { value: "gst-guides", label: "GST Guides", icon: Shield, color: "text-success" },
  { value: "accounting", label: "Accounting Tutorials", icon: Calculator, color: "text-accent" },
  { value: "tax-updates", label: "Tax Updates", icon: Landmark, color: "text-primary" },
  { value: "compliance", label: "Compliance Articles", icon: FileText, color: "text-warning" },
  { value: "business", label: "Business Tips", icon: Building2, color: "text-accent" },
  { value: "startup", label: "Startup Guide", icon: TrendingUp, color: "text-success" },
];

const AUDIENCES = [
  { value: "all", label: "Everyone" },
  { value: "accountants", label: "Accountants" },
  { value: "businesses", label: "Businesses" },
  { value: "cas", label: "Chartered Accountants" },
];

type Article = {
  id: string; author_user_id: string; author_name: string;
  title: string; slug: string; excerpt: string | null; content: string;
  category: string; tags: string[]; audience: string;
  cover_image_url: string | null; read_time_minutes: number;
  is_published: boolean; views: number; likes: number;
  created_at: string; updated_at: string;
};

type Video = {
  id: string; author_user_id: string; author_name: string;
  title: string; description: string | null; video_url: string;
  thumbnail_url: string | null; category: string; tags: string[];
  audience: string; duration_minutes: number;
  is_published: boolean; views: number; likes: number;
  created_at: string; updated_at: string;
};

export default function EducationHubPage() {
  const { user, role } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const canCreate = role === "super_admin" || role === "ca" || role === "company_admin";

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("articles");
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [showVideoDialog, setShowVideoDialog] = useState(false);
  const [readingArticle, setReadingArticle] = useState<Article | null>(null);

  const [articleForm, setArticleForm] = useState({
    title: "", excerpt: "", content: "", category: "gst-guides",
    tags: "", audience: "all", read_time_minutes: "5", is_published: true,
  });
  const [videoForm, setVideoForm] = useState({
    title: "", description: "", video_url: "", category: "gst-guides",
    tags: "", audience: "all", duration_minutes: "10", is_published: true,
  });

  // Fetch articles
  const { data: articles = [], isLoading: loadingArticles } = useQuery({
    queryKey: ["education-articles"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("education_articles")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Article[];
    },
  });

  // Fetch videos
  const { data: videos = [], isLoading: loadingVideos } = useQuery({
    queryKey: ["education-videos"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("education_videos")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Video[];
    },
  });

  // Create article
  const createArticle = useMutation({
    mutationFn: async () => {
      const slug = articleForm.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
      const { error } = await supabase.from("education_articles").insert({
        author_user_id: user!.id,
        author_name: user?.email?.split("@")[0] || "Author",
        title: articleForm.title,
        slug: slug + "-" + Date.now(),
        excerpt: articleForm.excerpt || null,
        content: articleForm.content,
        category: articleForm.category,
        tags: articleForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        audience: articleForm.audience,
        read_time_minutes: Number(articleForm.read_time_minutes) || 5,
        is_published: articleForm.is_published,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["education-articles"] });
      setShowArticleDialog(false);
      setArticleForm({ title: "", excerpt: "", content: "", category: "gst-guides", tags: "", audience: "all", read_time_minutes: "5", is_published: true });
      toast({ title: "Article published!" });
    },
    onError: () => toast({ title: "Error", description: "Failed to create article.", variant: "destructive" }),
  });

  // Create video
  const createVideo = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("education_videos").insert({
        author_user_id: user!.id,
        author_name: user?.email?.split("@")[0] || "Author",
        title: videoForm.title,
        description: videoForm.description || null,
        video_url: videoForm.video_url,
        category: videoForm.category,
        tags: videoForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
        audience: videoForm.audience,
        duration_minutes: Number(videoForm.duration_minutes) || 10,
        is_published: videoForm.is_published,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["education-videos"] });
      setShowVideoDialog(false);
      setVideoForm({ title: "", description: "", video_url: "", category: "gst-guides", tags: "", audience: "all", duration_minutes: "10", is_published: true });
      toast({ title: "Video tutorial added!" });
    },
    onError: () => toast({ title: "Error", description: "Failed to add video.", variant: "destructive" }),
  });

  // Filtering
  const filterItems = <T extends { category: string; audience: string; title: string; tags: string[] }>(items: T[]) =>
    items.filter((item) => {
      if (categoryFilter !== "all" && item.category !== categoryFilter) return false;
      if (audienceFilter !== "all" && item.audience !== audienceFilter && item.audience !== "all") return false;
      if (search) {
        const q = search.toLowerCase();
        return item.title.toLowerCase().includes(q) || item.tags.some((t) => t.toLowerCase().includes(q));
      }
      return true;
    });

  const filteredArticles = filterItems(articles);
  const filteredVideos = filterItems(videos);
  const catCfg = (cat: string) => CATEGORIES.find((c) => c.value === cat) || CATEGORIES[0];

  // Stats
  const totalContent = articles.length + videos.length;
  const totalViews = [...articles, ...videos].reduce((sum, i) => sum + i.views, 0);
  const totalLikes = [...articles, ...videos].reduce((sum, i) => sum + i.likes, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground flex items-center gap-2">
            <GraduationCap className="h-6 w-6 text-accent" /> Education Hub
          </h1>
          <p className="text-sm text-muted-foreground">Learn accounting, GST, tax planning, and compliance with expert resources</p>
        </div>
        {canCreate && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowVideoDialog(true)}>
              <Play className="h-4 w-4 mr-1" /> Add Video
            </Button>
            <Button size="sm" onClick={() => setShowArticleDialog(true)}>
              <Plus className="h-4 w-4 mr-1" /> Write Article
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Total Resources", value: totalContent, icon: BookOpen, color: "text-accent" },
          { label: "Articles", value: articles.length, icon: FileText, color: "text-success" },
          { label: "Video Tutorials", value: videos.length, icon: Play, color: "text-primary" },
          { label: "Total Views", value: totalViews.toLocaleString("en-IN"), icon: Eye, color: "text-warning" },
        ].map((s, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
            <Card>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-muted-foreground">{s.label}</span>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <div className="text-xl font-bold font-display text-foreground">{s.value}</div>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Category Pills */}
      <div className="flex gap-2 flex-wrap">
        <Badge variant={categoryFilter === "all" ? "default" : "outline"} className="cursor-pointer px-3 py-1.5" onClick={() => setCategoryFilter("all")}>
          All Topics
        </Badge>
        {CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          return (
            <Badge key={cat.value} variant={categoryFilter === cat.value ? "default" : "outline"} className="cursor-pointer px-3 py-1.5 flex items-center gap-1.5" onClick={() => setCategoryFilter(cat.value)}>
              <Icon className="h-3 w-3" /> {cat.label}
            </Badge>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search articles, videos, topics..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={audienceFilter} onValueChange={setAudienceFilter}>
          <SelectTrigger className="w-[180px]">
            <Users className="h-3.5 w-3.5 mr-1 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="articles">
            <FileText className="h-3.5 w-3.5 mr-1" /> Articles ({filteredArticles.length})
          </TabsTrigger>
          <TabsTrigger value="videos">
            <Play className="h-3.5 w-3.5 mr-1" /> Video Tutorials ({filteredVideos.length})
          </TabsTrigger>
        </TabsList>

        {/* Articles Tab */}
        <TabsContent value="articles" className="mt-4">
          {loadingArticles ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading articles...</p>
          ) : filteredArticles.length === 0 ? (
            <Card className="text-center py-12">
              <BookOpen className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No articles found. {canCreate && "Be the first to write one!"}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence>
                {filteredArticles.map((article, i) => {
                  const cat = catCfg(article.category);
                  const CatIcon = cat.icon;
                  return (
                    <motion.div key={article.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: i * 0.03 }}>
                      <Card className="hover:border-accent/40 transition-colors cursor-pointer group" onClick={() => setReadingArticle(article)}>
                        {article.cover_image_url && (
                          <div className="w-full h-32 rounded-lg bg-muted/30 mb-3 overflow-hidden">
                            <img src={article.cover_image_url} alt={article.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                            <CatIcon className={`h-2.5 w-2.5 ${cat.color}`} /> {cat.label}
                          </Badge>
                          {article.audience !== "all" && (
                            <Badge variant="outline" className="text-[10px]">For {AUDIENCES.find((a) => a.value === article.audience)?.label}</Badge>
                          )}
                        </div>
                        <h4 className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors line-clamp-2">{article.title}</h4>
                        {article.excerpt && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{article.excerpt}</p>}
                        {article.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {article.tags.slice(0, 3).map((t, j) => (
                              <span key={j} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground flex items-center gap-0.5">
                                <Tag className="h-2 w-2" /> {t}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50 text-xs text-muted-foreground">
                          <span>{article.author_name}</span>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {article.read_time_minutes} min</span>
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {article.views}</span>
                            <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {article.likes}</span>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos" className="mt-4">
          {loadingVideos ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading videos...</p>
          ) : filteredVideos.length === 0 ? (
            <Card className="text-center py-12">
              <Play className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No video tutorials found. {canCreate && "Add the first tutorial!"}</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              <AnimatePresence>
                {filteredVideos.map((video, i) => {
                  const cat = catCfg(video.category);
                  const CatIcon = cat.icon;
                  return (
                    <motion.div key={video.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ delay: i * 0.03 }}>
                      <Card className="hover:border-accent/40 transition-colors cursor-pointer group">
                        <div className="relative w-full h-36 rounded-lg bg-muted/30 mb-3 overflow-hidden flex items-center justify-center">
                          {video.thumbnail_url ? (
                            <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-accent/10 to-primary/10 flex items-center justify-center">
                              <Play className="h-10 w-10 text-accent/60" />
                            </div>
                          )}
                          <div className="absolute bottom-2 right-2 bg-foreground/80 text-background text-[10px] px-1.5 py-0.5 rounded">
                            {video.duration_minutes} min
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-[10px] flex items-center gap-1">
                            <CatIcon className={`h-2.5 w-2.5 ${cat.color}`} /> {cat.label}
                          </Badge>
                        </div>
                        <h4 className="text-sm font-semibold text-foreground group-hover:text-accent transition-colors line-clamp-2">{video.title}</h4>
                        {video.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{video.description}</p>}
                        <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50 text-xs text-muted-foreground">
                          <span>{video.author_name}</span>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {video.views}</span>
                            <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {video.likes}</span>
                          </div>
                        </div>
                        <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-accent hover:underline" onClick={(e) => e.stopPropagation()}>
                          <ExternalLink className="h-3 w-3" /> Watch Video
                        </a>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Article Reader Dialog */}
      <Dialog open={!!readingArticle} onOpenChange={() => setReadingArticle(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {readingArticle && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px]">{catCfg(readingArticle.category).label}</Badge>
                  {readingArticle.audience !== "all" && (
                    <Badge variant="outline" className="text-[10px]">For {AUDIENCES.find((a) => a.value === readingArticle.audience)?.label}</Badge>
                  )}
                </div>
                <DialogTitle className="text-lg">{readingArticle.title}</DialogTitle>
                <DialogDescription>
                  By {readingArticle.author_name} · {readingArticle.read_time_minutes} min read · {new Date(readingArticle.created_at).toLocaleDateString("en-IN")}
                </DialogDescription>
              </DialogHeader>
              {readingArticle.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {readingArticle.tags.map((t, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted/30 text-muted-foreground">{t}</span>
                  ))}
                </div>
              )}
              <div className="prose prose-sm max-w-none text-foreground">
                {readingArticle.content.split("\n").map((para, i) => (
                  <p key={i} className="text-sm text-muted-foreground leading-relaxed mb-3">{para}</p>
                ))}
              </div>
              <div className="flex items-center gap-4 pt-3 border-t border-border text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Eye className="h-3 w-3" /> {readingArticle.views} views</span>
                <span className="flex items-center gap-1"><Heart className="h-3 w-3" /> {readingArticle.likes} likes</span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Write Article Dialog */}
      <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Write an Article</DialogTitle>
            <DialogDescription>Share your expertise with the AccouPro community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Title *</label>
              <Input value={articleForm.title} onChange={(e) => setArticleForm({ ...articleForm, title: e.target.value })} placeholder="e.g., Complete Guide to GSTR-1 Filing" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Excerpt</label>
              <Input value={articleForm.excerpt} onChange={(e) => setArticleForm({ ...articleForm, excerpt: e.target.value })} placeholder="Brief summary (shown in cards)" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Category *</label>
                <Select value={articleForm.category} onValueChange={(v) => setArticleForm({ ...articleForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Audience</label>
                <Select value={articleForm.audience} onValueChange={(v) => setArticleForm({ ...articleForm, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Content *</label>
              <Textarea value={articleForm.content} onChange={(e) => setArticleForm({ ...articleForm, content: e.target.value })} placeholder="Write your article content..." rows={8} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Tags (comma-separated)</label>
                <Input value={articleForm.tags} onChange={(e) => setArticleForm({ ...articleForm, tags: e.target.value })} placeholder="GST, Filing, Returns" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Read Time (min)</label>
                <Input type="number" value={articleForm.read_time_minutes} onChange={(e) => setArticleForm({ ...articleForm, read_time_minutes: e.target.value })} />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={articleForm.is_published} onCheckedChange={(v) => setArticleForm({ ...articleForm, is_published: v })} />
              <Label className="text-xs">Publish immediately</Label>
            </div>
            <Button className="w-full" disabled={!articleForm.title || !articleForm.content || createArticle.isPending} onClick={() => createArticle.mutate()}>
              <Pencil className="h-4 w-4 mr-2" />
              {createArticle.isPending ? "Publishing..." : "Publish Article"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Video Dialog */}
      <Dialog open={showVideoDialog} onOpenChange={setShowVideoDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Video Tutorial</DialogTitle>
            <DialogDescription>Share a video tutorial with the community</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Title *</label>
              <Input value={videoForm.title} onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })} placeholder="e.g., How to File GSTR-3B Step by Step" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Video URL * (YouTube, Vimeo, etc.)</label>
              <Input value={videoForm.video_url} onChange={(e) => setVideoForm({ ...videoForm, video_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Textarea value={videoForm.description} onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })} placeholder="What will viewers learn..." rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Category</label>
                <Select value={videoForm.category} onValueChange={(v) => setVideoForm({ ...videoForm, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Audience</label>
                <Select value={videoForm.audience} onValueChange={(v) => setVideoForm({ ...videoForm, audience: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {AUDIENCES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Duration (min)</label>
                <Input type="number" value={videoForm.duration_minutes} onChange={(e) => setVideoForm({ ...videoForm, duration_minutes: e.target.value })} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Tags (comma-separated)</label>
                <Input value={videoForm.tags} onChange={(e) => setVideoForm({ ...videoForm, tags: e.target.value })} placeholder="GST, Tutorial" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={videoForm.is_published} onCheckedChange={(v) => setVideoForm({ ...videoForm, is_published: v })} />
              <Label className="text-xs">Publish immediately</Label>
            </div>
            <Button className="w-full" disabled={!videoForm.title || !videoForm.video_url || createVideo.isPending} onClick={() => createVideo.mutate()}>
              <Play className="h-4 w-4 mr-2" />
              {createVideo.isPending ? "Adding..." : "Add Video Tutorial"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
